import { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';

interface Ember {
  x: number;
  y: number;
  size: number;
  speedY: number;
  speedX: number;
  opacity: number;
  targetOpacity: number;
  fadeSpeed: number;
  color: string;
  wobbleSpeed: number;
  wobbleRadius: number;
  wobbleAngle: number;
}

const EMBER_COLORS = [
  '248, 212, 40',   // Ouro UCC
  '255, 170, 30',   // Âmbar quente
  '255, 120, 20',   // Fagulha de fogo laranja
  '255, 235, 120',  // Brilho dourado claro
  '230, 90, 15',    // Brasa profunda
];

export function HeroEmbers({
  /**
   * Teto de fagulhas. A hero usa o padrão; um card pequeno pede bem menos —
   * a mesma densidade num retângulo de 300px vira fumaça em vez de brasa, e
   * paga um laço de animação por card na tela.
   */
  quantidade = 50,
  className,
}: {
  quantidade?: number;
  className?: string;
} = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = canvas.parentElement?.clientWidth || window.innerWidth);
    let height = (canvas.height = canvas.parentElement?.clientHeight || window.innerHeight);

    const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isReducedMotion) return;

    // Criar array de fagulhas de fogo sutis
    const count = Math.max(4, Math.min(quantidade, Math.floor(width / 32)));
    const embers: Ember[] = [];

    const createEmber = (initialY?: number): Ember => {
      const color = EMBER_COLORS[Math.floor(Math.random() * EMBER_COLORS.length)]!;
      return {
        x: Math.random() * width,
        y: initialY !== undefined ? initialY : height + Math.random() * 40,
        size: Math.random() * 2.2 + 1.2,
        speedY: Math.random() * 0.45 + 0.25,
        speedX: (Math.random() - 0.5) * 0.2,
        opacity: Math.random() * 0.2,
        targetOpacity: Math.random() * 0.65 + 0.25,
        fadeSpeed: Math.random() * 0.01 + 0.004,
        color,
        wobbleSpeed: Math.random() * 0.02 + 0.01,
        wobbleRadius: Math.random() * 1.2 + 0.5,
        wobbleAngle: Math.random() * Math.PI * 2,
      };
    };

    // Inicializar fagulhas distribuídas pela tela
    for (let i = 0; i < count; i++) {
      embers.push(createEmber(Math.random() * height));
    }

    /*
     * Cada cor vira um carimbo desenhado uma vez.
     *
     * O laço criava um `createRadialGradient` por fagulha, a cada quadro:
     * cinquenta objetos de gradiente por quadro, três mil por segundo, todos
     * descartados em seguida. Além do custo em si, era lixo suficiente para o
     * coletor entrar em ação no meio da rolagem — e é assim que uma animação
     * leve produz engasgo.
     *
     * Com o carimbo pronto, desenhar uma fagulha vira um `drawImage` escalado,
     * que a GPU resolve. A opacidade sai do `globalAlpha`, então uma única
     * imagem serve para todos os estágios de brilho da mesma cor.
     */
    const RAIO_CARIMBO = 32;
    const carimbos = new Map<string, HTMLCanvasElement>();
    for (const cor of EMBER_COLORS) {
      const selo = document.createElement('canvas');
      selo.width = selo.height = RAIO_CARIMBO * 2;
      const sctx = selo.getContext('2d');
      if (!sctx) continue;

      const grad = sctx.createRadialGradient(
        RAIO_CARIMBO,
        RAIO_CARIMBO,
        0,
        RAIO_CARIMBO,
        RAIO_CARIMBO,
        RAIO_CARIMBO,
      );
      grad.addColorStop(0, `rgba(${cor}, 1)`);
      grad.addColorStop(0.4, `rgba(${cor}, 0.6)`);
      grad.addColorStop(1, `rgba(${cor}, 0)`);
      sctx.fillStyle = grad;
      sctx.fillRect(0, 0, selo.width, selo.height);

      // Núcleo claro, no mesmo carimbo: a fagulha inteira num desenho só.
      sctx.globalCompositeOperation = 'lighter';
      const nucleo = sctx.createRadialGradient(
        RAIO_CARIMBO,
        RAIO_CARIMBO,
        0,
        RAIO_CARIMBO,
        RAIO_CARIMBO,
        RAIO_CARIMBO * 0.22,
      );
      nucleo.addColorStop(0, 'rgba(255, 255, 220, 0.9)');
      nucleo.addColorStop(1, 'rgba(255, 255, 220, 0)');
      sctx.fillStyle = nucleo;
      sctx.fillRect(0, 0, selo.width, selo.height);

      carimbos.set(cor, selo);
    }

    let isVisible = true;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) isVisible = entry.isIntersecting;
      },
      { threshold: 0.05 },
    );
    observer.observe(canvas);

    const render = () => {
      animationFrameId = requestAnimationFrame(render);
      if (!isVisible) return;

      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 1;

      for (let i = 0; i < embers.length; i++) {
        const e = embers[i]!;

        // Movimento ascendente e oscilação orgânica (wobble)
        e.y -= e.speedY;
        e.wobbleAngle += e.wobbleSpeed;
        e.x += e.speedX + Math.sin(e.wobbleAngle) * e.wobbleRadius * 0.25;

        // Fade in / Fade out
        if (e.opacity < e.targetOpacity) {
          e.opacity += e.fadeSpeed;
        }

        // Perto do topo ou das bordas, desvanece
        if (e.y < height * 0.2) {
          e.opacity -= e.fadeSpeed * 1.5;
        }

        // Desenhar a fagulha: um `drawImage` do carimbo da cor dela.
        if (e.opacity > 0.01) {
          const selo = carimbos.get(e.color);
          if (selo) {
            const raio = e.size * 2.8;
            ctx.globalAlpha = e.opacity;
            ctx.drawImage(selo, e.x - raio, e.y - raio, raio * 2, raio * 2);
          }
        }

        // Reciclar fagulha ao sair pelo topo ou apagar
        if (e.y < -10 || e.opacity <= 0.01) {
          embers[i] = createEmber();
        }
      }
    };

    render();

    const handleResize = () => {
      if (!canvas.parentElement) return;
      width = canvas.width = canvas.parentElement.clientWidth;
      height = canvas.height = canvas.parentElement.clientHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [quantidade]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ transform: 'translateZ(0)' }}
      className={cn('pointer-events-none absolute inset-0 z-0 h-full w-full opacity-80', className)}
    />
  );
}
